// Collection Price Updater - Fetches and displays minimum prices from Webbee API
class CollectionPriceUpdater {
    constructor() {
        this.priceCache = new Map();
        this.processingSkus = new Set();
        this.observer = null;
        this.API_TIMEOUT = 10000;
        this.BATCH_DELAY = 100;
        this.pendingUpdates = [];
        this.customerTag = this.getCustomerTag();
        this.customerNSID = this.getCustomerNSID();
        
        console.log('Collection Price Updater - Customer Tag:', this.customerTag);
        console.log('Collection Price Updater - Customer NSID:', this.customerNSID);
        
        this.init();
    }

    getCustomerTag() {
        if (window.customerPriceLevel) {
            return window.customerPriceLevel;
        }
        
        if (window.customerTag) {
            if (Array.isArray(window.customerTag)) {
                const priceLevelTag = window.customerTag.find(tag => tag.includes('pricelevel_'));
                if (priceLevelTag) {
                    const match = priceLevelTag.match(/pricelevel_(\d+)/);
                    return match ? match[1] : '3';
                }
            } else {
                return window.customerTag;
            }
        }

        const metaTag = document.querySelector('meta[name="customer-price-level"]');
        if (metaTag) {
            return metaTag.content;
        }
        return '3';
    }

    getCustomerNSID() {
        if (window.NSID && window.NSID !== '') {
            return window.NSID;
        }

        if (window.customerNSID && window.customerNSID !== '') {
            return window.customerNSID;
        }

        if (window.productNSID && window.productNSID !== '') {
            return window.productNSID;
        }

        const sessionNSID = sessionStorage.getItem('customerNSID');
        if (sessionNSID && sessionNSID !== '') {
            console.log('Using NSID from sessionStorage:', sessionNSID);
            return sessionNSID;
        }

        const metaTag = document.querySelector('meta[name="customer-nsid"]');
        if (metaTag && metaTag.content !== '') {
            return metaTag.content;
        }

        if (typeof CHECKOUT_CONFIG !== 'undefined' && CHECKOUT_CONFIG.NSID) {
            return CHECKOUT_CONFIG.NSID;
        }

        console.warn('Customer NSID not found - API calls may fail');
        console.warn('Checked: window.NSID, window.customerNSID, window.productNSID, sessionStorage, meta tags, CHECKOUT_CONFIG');
        return '';
    }

    init() {
        console.log('Collection Price Updater initialized');

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.updateAllVisiblePrices();
            });
        } else {
            this.updateAllVisiblePrices();
        }

        this.observeProductAdditions();

        this.handleCollectionUpdates();
    }

    observeProductAdditions() {
        const targetNode = document.querySelector('collection-products, #collection-products, [data-element="products-container"]');
        
        if (!targetNode) {
            console.warn('Collection products container not found for observer');
            const fallbackTarget = document.querySelector('.collection, main');
            if (!fallbackTarget) return;
            this.setupObserver(fallbackTarget);
            return;
        }

        this.setupObserver(targetNode);
    }

    setupObserver(targetNode) {
        this.observer = new MutationObserver((mutations) => {
            let hasNewProducts = false;
            
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        if (node.classList && (node.classList.contains('#product-card') || node.dataset.productCard !== undefined)) {
                            hasNewProducts = true;
                        } else if (node.querySelectorAll) {
                            const newCards = node.querySelectorAll('.\\#product-card, [data-product-card]');
                            if (newCards.length > 0) {
                                hasNewProducts = true;
                            }
                        }
                    }
                });
            });

            if (hasNewProducts) {
                console.log('New products detected, updating prices...');
                setTimeout(() => this.updateAllVisiblePrices(), this.BATCH_DELAY);
            }
        });

        this.observer.observe(targetNode, {
            childList: true,
            subtree: true
        });
    }

    handleCollectionUpdates() {
        document.addEventListener('collection:updated', () => {
            console.log('Collection updated, refreshing prices...');
            setTimeout(() => this.updateAllVisiblePrices(), 200);
        });

        document.addEventListener('shopify:section:load', () => {
            console.log('Section loaded, refreshing prices...');
            setTimeout(() => this.updateAllVisiblePrices(), 200);
        });
    }

    async updateAllVisiblePrices() {
        const productCards = this.getProductCards();
        
        if (productCards.length === 0) {
            console.log('No product cards found');
            return;
        }

        console.log(`Found ${productCards.length} product cards to update`);
        const skuGroups = this.groupCardsBySku(productCards);
        
        console.log(`Grouped into ${skuGroups.size} unique SKUs`);

        await this.fetchAndUpdatePrices(skuGroups);
    }

    getProductCards() {
        const selectors = [
            '.\\#product-card',
            '[data-product-card]',
            '.product-card'
        ];

        let cards = [];
        for (const selector of selectors) {
            const found = document.querySelectorAll(selector);
            if (found.length > 0) {
                cards = Array.from(found);
                break;
            }
        }

        return cards;
    }

    groupCardsBySku(productCards) {
        const skuGroups = new Map();

        productCards.forEach(card => {
            const sku = this.extractSku(card);
            
            if (!sku) {
                console.warn('Could not extract SKU from product card:', card);
                return;
            }

            if (!skuGroups.has(sku)) {
                skuGroups.set(sku, []);
            }

            skuGroups.get(sku).push(card);
        });

        return skuGroups;
    }

    extractSku(card) {
        if (card.dataset.sku) {
            return card.dataset.sku.trim();
        }
        const skuElement = card.querySelector('[data-product-sku]');
        if (skuElement && skuElement.textContent.trim()) {
            return skuElement.textContent.trim();
        }
        const skuElementLegacy = card.querySelector('.\\#product-card-sku, .product-card-sku');
        if (skuElementLegacy && skuElementLegacy.textContent.trim()) {
            return skuElementLegacy.textContent.trim();
        }
        const img = card.querySelector('img');
        if (img && img.getAttribute('src')) {
            const filename = img.getAttribute('src').split('/').pop() || '';
            const skuFromImage = filename.split('_')[0];
            if (skuFromImage && !skuFromImage.toLowerCase().includes('max') && skuFromImage.length > 2) {
                return skuFromImage;
            }
        }

        return null;
    }

    async fetchAndUpdatePrices(skuGroups) {
        const promises = [];

        for (const [sku, cards] of skuGroups.entries()) {
            if (this.processingSkus.has(sku)) {
                console.log(`Skipping ${sku} - already processing`);
                continue;
            }

            this.processingSkus.add(sku);

            const promise = this.fetchSkuMinPrice(sku)
                .then(result => {
                    if (result && result.minPrice !== null) {
                        console.log(`Fetched min price for SKU ${sku}: $${result.minPrice}`);
                        cards.forEach(card => {
                            this.updateCardPrice(card, result.minPrice, sku);
                        });
                    } else {
                        console.warn(`No valid price data for SKU ${sku}`);
                    }
                })
                .catch(error => {
                    console.error(`Failed to fetch price for SKU ${sku}:`, error);
                })
                .finally(() => {
                    this.processingSkus.delete(sku);
                });

            promises.push(promise);
        }

        await Promise.allSettled(promises);
        console.log('Price update completed');
    }

    async fetchSkuMinPrice(sku) {
        if (this.priceCache.has(sku)) {
            console.log(`Using cached price for SKU ${sku}`);
            return this.priceCache.get(sku);
        }

        return new Promise((resolve) => {
            const requestData = {
                skuAndDomainName: {
                    productSKU: [sku],
                    domainName: "ace-products-group.myshopify.com",
                    tag: this.customerTag,  
                    NSID: this.customerNSID  
                }
            };

            console.log(`Fetching price for SKU ${sku} with params:`, requestData);

            const timeoutId = setTimeout(() => {
                console.warn(`Timeout fetching price for SKU ${sku}`);
                resolve(null);
            }, this.API_TIMEOUT);

            $.ajax({
                type: 'GET',
                url: "https://hooks.webbeeglobal.com/NetsuiteSuitetalkWeb/quantity-pricing/product",
                dataType: 'json',
                cache: false,
                data: encodeURIComponent(JSON.stringify(requestData)),
                success: (data) => {
                    clearTimeout(timeoutId);
                    
                    console.log(`API Response for SKU ${sku}:`, data);
                    
                    try {
                        if (!data || !data.productValues || data.productValues === "null" || data.productValues === "") {
                            console.warn(`Empty or null productValues for SKU ${sku}`);
                            resolve(null);
                            return;
                        }

                        const responseJSON = JSON.parse(data.productValues);
                        const skuData = responseJSON[sku] || [];
                        
                        if (skuData.length > 0) {
                            const result = this.findMinimumPrice(skuData);
                            
                            if (result !== null) {
                                this.priceCache.set(sku, result);
                                console.log(`Successfully cached price for SKU ${sku}: $${result.minPrice}`);
                                resolve(result);
                            } else {
                                console.warn(`Could not determine min price for SKU ${sku}`);
                                resolve(null);
                            }
                        } else {
                            console.warn(`No pricing tiers for SKU ${sku}`);
                            resolve(null);
                        }
                    } catch (error) {
                        console.error(`Parse error for SKU ${sku}:`, error);
                        console.error('Raw response:', data);
                        resolve(null);
                    }
                },
                error: (xhr, status, error) => {
                    clearTimeout(timeoutId);
                    console.error(`API error for SKU ${sku}:`, status, error);
                    console.error('XHR:', xhr);
                    resolve(null);
                }
            });
        });
    }

    findMinimumPrice(skuData) {
        if (!skuData || skuData.length === 0) return null;
        let minPrice = Infinity;
        
        skuData.forEach(tier => {
            const price = parseFloat(tier.amount);
            if (!isNaN(price) && price < minPrice) {
                minPrice = price;
            }
        });

        if (minPrice === Infinity) return null;

        return {
            minPrice: minPrice,
            allTiers: skuData
        };
    }

    updateCardPrice(card, minPrice, sku) {
        const priceSelectors = [
            '[data-product-price]',
            '.\\#product-card-price',
            '.product-card-price',
            '.price'
        ];

        let priceElement = null;
        for (const selector of priceSelectors) {
            priceElement = card.querySelector(selector);
            if (priceElement) break;
        }

        if (!priceElement) {
            console.warn('Could not find price element in card for SKU:', sku);
            return;
        }
        if (!priceElement.dataset.originalPrice) {
            priceElement.dataset.originalPrice = priceElement.innerHTML;
        }

        const formattedPrice = this.formatPrice(minPrice);
        
        priceElement.innerHTML = formattedPrice;
        priceElement.dataset.webbeePrice = minPrice.toFixed(2);
        priceElement.dataset.sku = sku;
        
        priceElement.classList.add('webbee-price-updated');
        
        console.log(`✓ Updated price for SKU ${sku}: ${formattedPrice}`);
    }

    formatPrice(price) {
        return `$${price.toFixed(2)}`;
    }

    clearCache() {
        this.priceCache.clear();
        console.log('Price cache cleared');
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

let collectionPriceUpdater;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        collectionPriceUpdater = new CollectionPriceUpdater();
    });
} else {
    collectionPriceUpdater = new CollectionPriceUpdater();
}

window.collectionPriceUpdater = collectionPriceUpdater;

window.updateCollectionPrices = function() {
    if (collectionPriceUpdater) {
        collectionPriceUpdater.updateAllVisiblePrices();
    }
};

window.clearCollectionPriceCache = function() {
    if (collectionPriceUpdater) {
        collectionPriceUpdater.clearCache();
        collectionPriceUpdater.updateAllVisiblePrices();
    }
};